import { useSearchParams } from 'react-router-dom';
import { useDrawerStore } from '../../../store';
import {
  useDeleteData,
  useDepartmentStore,
  useFetchData,
} from '../../../hooks';
import React, { useEffect, useState } from 'react';
import { DepartmentModel, PageableResponse } from '../../../types';
import { Resource } from '../../../lib/enums';
import { convertDate, dateTimeFormat } from '../../../lib/utils';
import { Spin } from 'antd';
import BaseTable from '../../../components/BaseTable';
import InfoModal from '../../../components/ui/modal/InfoModal';
import { ModuleAccess } from '../../../types/auth.model';

const DepartmentTable = ({
  api,
  moduleName,
  userRole,
  userModuleAccess,
}: {
  api: string;
  moduleName: string;
  userRole: string | undefined;
  userModuleAccess: ModuleAccess | undefined;
}) => {
  const [searchParams] = useSearchParams();
  const drawerStore = useDrawerStore();
  const departmentStore = useDepartmentStore();
  const [spinning, setSpinning] = useState<boolean>(false);
  const [isInfoModalOpen, setInfoModalOpen] = useState(false);

  const { data, isLoading } = useFetchData<PageableResponse<DepartmentModel>>(
    api,
    {
      resource: searchParams.get('resource') || Resource.FULL,
      search: searchParams.get('search') || '',
      length: parseInt(searchParams.get('length') || '999999'),
    },
  );

  const {
    mutate,
    isPending: isPendingDelete,
    isSuccess: isSuccessDelete,
  } = useDeleteData(api);

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
    },
    {
      title: 'Description',
      dataIndex: 'description',
    },
    {
      title: 'Default Location',
      dataIndex: 'defaultLocation',
      render: (_text: string, record: DepartmentModel) =>
        `${record?.defaultLocation?.description === undefined ? '' : record.defaultLocation?.description}`,
    },
    {
      title: 'Categories',
      dataIndex: 'categories',
      render: (_text: string, record: DepartmentModel) =>
        record?.categories?.map((category) => category.description).join(', '),
    },
    {
      title: 'Created By/Date',
      dataIndex: 'createdBy',
      render: (_text: string, record: DepartmentModel) => {
        return (
          <div className="flex flex-col">
            <span className="mr-2">{record.createdBy}</span>
            {record.timeCreated != null && (
              <span>{convertDate(record.timeCreated, dateTimeFormat)}</span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Update By/Date',
      dataIndex: 'updatedBy',
      render: (_text: string, record: DepartmentModel) => {
        return (
          <div className="flex flex-col">
            <span className="mr-2">{record.updatedBy}</span>
            {record.timeUpdated != null && (
              <span>{convertDate(record.timeUpdated, dateTimeFormat)}</span>
            )}
          </div>
        );
      },
    },
  ];

  const handleDataDelete = (data: DepartmentModel) => {
    mutate({ id: data.id });
    setSpinning(true);
  };

  const handleDataEdit = (data: DepartmentModel) => {
    departmentStore.setData(data);
    drawerStore.onClose();
  };

  useEffect(() => {
    if (isSuccessDelete) setSpinning(false);
  }, [isSuccessDelete]);

  const handleInfoModalClose = () => {
    setInfoModalOpen(false);
  };

  return (
    <>
      <Spin spinning={spinning} fullscreen />
      <BaseTable
        dataSource={data?.data || []}
        isLoading={isLoading || isPendingDelete}
        handleDeleteButtonClick={handleDataDelete}
        handleEditButtonClick={handleDataEdit}
        columns={columns}
        canDelete={userModuleAccess?.delete}
        canEdit={userModuleAccess?.write}
        userRole={userRole}
        module={moduleName}
      />
      <InfoModal
        visible={isInfoModalOpen}
        message={`${moduleName} successfully deleted`}
        onClose={handleInfoModalClose}
      />
    </>
  );
};

export default DepartmentTable;
